import React, { useState } from 'react';
import { TextField, Typography, Box, Paper } from '@mui/material';

export interface CreateTenantRequest {
  name: string;
  email: string;
}

interface DetailsEditorActions {
  onSave?: () => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onCancel?: () => void;
}

interface DetailsEditorState {
  saveEnabled?: boolean;
  deleteEnabled?: boolean;
  saving?: boolean;
  deleting?: boolean;
}

interface TenantCreateProps {
  editorActions?: DetailsEditorActions;
  setEditorState?: (state: Partial<DetailsEditorState>) => void;
  onSave?: (data: CreateTenantRequest) => Promise<void>;
}

const TenantCreate: React.FC<TenantCreateProps> = ({ editorActions = {}, setEditorState = () => {}, onSave }) => {
  const [formData, setFormData] = useState<CreateTenantRequest>({ name: '', email: '' });

  const isFormValid = formData.name.trim() !== '' && 
    formData.email.trim() !== '' && 
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());

  // Update editor state based on form validation
  React.useEffect(() => {
    setEditorState({ saveEnabled: isFormValid });
  }, [isFormValid, setEditorState]);

  // Register save action with parent's business logic
  React.useEffect(() => {
    if (onSave) {
      editorActions.onSave = async () => {
        setEditorState({ saving: true });
        try {
          await onSave(formData);
        } finally {
          setEditorState({ saving: false });
        }
      };
    }
  }, [editorActions, onSave, formData, setEditorState]);

  // Register cancel action
  React.useEffect(() => {
    editorActions.onCancel = () => {
      // Cancel action will be handled by parent
    };
  }, [editorActions]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && editorActions.onSave) {
      e.preventDefault();
      editorActions.onSave();
    } else if (e.key === 'Escape' && editorActions.onCancel) {
      e.preventDefault();
      editorActions.onCancel();
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Create Tenant
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onKeyDown={handleKeyDown} tabIndex={0}>
        <TextField
          label="Name"
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
          required
          fullWidth
          variant="outlined"
          error={!formData.name.trim()}
          helperText={!formData.name.trim() ? 'Name is required' : ''}
        />
        <TextField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, email: e.target.value })}
          required
          fullWidth
          variant="outlined"
          error={!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())}
          helperText={!formData.email.trim() ? 'Email is required' : (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) ? 'Invalid email format' : '')}
        />
      </Box>
    </Paper>
  );
};

export default TenantCreate;